import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { DepreciationPeriodRestDataProvider } from '../../../data/DepreciationPeriodRestDataProvider';
import { DepreciationPeriod } from '../../../models/DepreciationPeriod';
import './DepreciationPeriodsTab.scss';
import { useTranslation } from 'react-i18next';

const DepreciationPeriodsTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [periods, setPeriods] = useState<DepreciationPeriod[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const provider = React.useMemo(() => new DepreciationPeriodRestDataProvider(), []);

    const columns: DetailedListColumn<DepreciationPeriod>[] = React.useMemo(() => [
        { key: 'name',            header: t('screens:depreciationPeriods.columns.name'),            dataField: 'name',            type: 'string', required: true },
        { key: 'years',           header: t('screens:depreciationPeriods.columns.years'),           dataField: 'years',           type: 'number', required: true },
        { key: 'thresholdAmount', header: t('screens:depreciationPeriods.columns.thresholdAmount'), dataField: 'thresholdAmount', type: 'number', required: true },
    ], [t]);

    useEffect(() => {
        setLoading(true);
        provider.getAll()
            .then(setPeriods)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [provider]);

    const handleChange = async (newPeriods: DepreciationPeriod[], action: 'add' | 'edit' | 'delete', changed: DepreciationPeriod) => {
        try {
            if (action === 'add') {
                await provider.add(changed);
            } else if (action === 'edit') {
                await provider.update(changed.id, changed);
            } else if (action === 'delete') {
                await provider.delete(changed.id);
            }

            const refreshed = await provider.getAll();
            setPeriods(refreshed);
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div className="depreciation-periods-tab">
            <SubsectionPanel title={t('screens:depreciationPeriods.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <InfoPanel header={t('screens:depreciationPeriods.infoHeader')}>
                    {t('screens:depreciationPeriods.infoBody')}
                </InfoPanel>
                <DetailedListComponent
                    dataSource={{ data: periods, setData: setPeriods }}
                    columns={columns}
                    rowKey={r => r.id}
                    onChange={handleChange}
                />
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>
        </div>
    );
};

export default DepreciationPeriodsTab;

